import {
  Badge,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title
} from '@mantine/core'
import { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { CopyableText } from '@/components/ui-shared/CopyableText/CopyableText'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { DeploymentStatus } from '@/types/deployment'
import { DeploymentGroup } from '@/types/deploymentGroup'
import { Device } from '@/types/device'
import { getDeviceGpus } from '@/utils/device/getDeviceAttr'
import { DeploymentGroupLoadCard } from './DeploymentGroupLoadCard'
import { EngineBuildStatus } from './EngineBuildStatus'
import { GroupWarningBadge } from './GroupWarningBadge'
import { getGroupErrorMessageId } from './groupErrorMessages'

type SectionProps = {
  titleId: string
  children: ReactNode
}

const Section = ({ titleId, children }: SectionProps) => (
  <Paper p="md" withBorder>
    <Title order={6} mb="xs">
      <FormattedMessage id={titleId} />
    </Title>
    {children}
  </Paper>
)

type SettingRowProps = {
  labelId: string
  children: ReactNode
}

const SettingRow = ({ labelId, children }: SettingRowProps) => (
  <Table.Tr>
    <Table.Th w={160}>
      <FormattedMessage id={labelId} />
    </Table.Th>
    <Table.Td>{children}</Table.Td>
  </Table.Tr>
)

type DeploymentGroupOverviewTabProps = {
  group: DeploymentGroup
  device?: Device
}

export const DeploymentGroupOverviewTab = ({
  group,
  device
}: DeploymentGroupOverviewTabProps) => {
  const { gpu, allowed_gpus, engine, settings, warnings, tracked } = group
  const trackingState = !tracked
    ? 'off'
    : warnings.some((warning) => warning.code === 'tracking_off')
      ? 'notRunning'
      : 'on'
  const deviceGpus = device ? getDeviceGpus(device) : []
  const errorMessageId = getGroupErrorMessageId(group.error_code)
  const hasError = !!group.error_code || !!group.error_body
  const hasPendingUpdate = group.applied_revision !== group.revision

  return (
    <Stack gap="md">
      {hasError && (
        <PlatformAlert
          variant={
            group.status === DeploymentStatus.Failed ? 'error' : 'warning'
          }
          title={errorMessageId && <FormattedMessage id={errorMessageId} />}
        >
          {group.error_body || group.error_code}
        </PlatformAlert>
      )}

      {warnings.length > 0 && (
        <PlatformAlert
          variant="warning"
          title={<FormattedMessage id="deployments.groups.details.warnings" />}
        >
          <Stack gap="xs">
            {warnings.map((warning) => (
              <GroupWarningBadge key={warning.code} warning={warning} />
            ))}
          </Stack>
        </PlatformAlert>
      )}

      {hasPendingUpdate && (
        <PlatformAlert variant="info">
          {group.applied_revision === null ? (
            <FormattedMessage
              id="deployments.groups.details.revisionPending.notApplied"
              values={{ revision: group.revision }}
            />
          ) : (
            <FormattedMessage
              id="deployments.groups.details.revisionPending"
              values={{
                applied: group.applied_revision,
                revision: group.revision
              }}
            />
          )}
        </PlatformAlert>
      )}

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <Section titleId="deployments.groups.details.gpu">
          <Stack gap="xs">
            <Text size="sm" fw={600}>
              {gpu ? (
                <FormattedMessage
                  id="deployments.list.group.gpu"
                  values={{ index: gpu.index, name: gpu.name }}
                />
              ) : (
                <FormattedMessage id="deployments.list.group.gpuNotChosen" />
              )}
            </Text>

            <Text size="xs" c="dimmed">
              {!gpu && (
                <FormattedMessage id="deployments.groups.details.gpu.notChosen" />
              )}
              {gpu && gpu.pinned_by_override && (
                <FormattedMessage id="deployments.groups.details.gpu.pinned" />
              )}
              {gpu && !gpu.pinned_by_override && (
                <FormattedMessage id="deployments.groups.details.gpu.chosenByDevice" />
              )}
            </Text>

            <Group gap="xs">
              <Text size="xs" c="dimmed">
                <FormattedMessage id="deployments.groups.details.allowedGpus" />
              </Text>

              {!allowed_gpus?.length && (
                <Text size="xs">
                  <FormattedMessage id="deployments.groups.details.allowedGpus.none" />
                </Text>
              )}

              {allowed_gpus?.map((index) => {
                const name = deviceGpus.find((g) => g.index === index)?.name
                return (
                  <Badge key={index} variant="light" color="gray" size="sm">
                    {name ? (
                      <FormattedMessage
                        id="deployments.list.group.gpu"
                        values={{ index, name }}
                      />
                    ) : (
                      <FormattedMessage
                        id="deployments.groups.details.allowedGpus.unnamed"
                        values={{ index }}
                      />
                    )}
                  </Badge>
                )
              })}
            </Group>
          </Stack>
        </Section>

        <Section titleId="deployments.groups.details.engine">
          <EngineBuildStatus engine={engine} gpuIndex={gpu?.index} />
        </Section>
      </SimpleGrid>

      <Section titleId="deployments.groups.details.settings">
        <Table variant="vertical" layout="fixed">
          <Table.Tbody>
            <SettingRow labelId="deployments.groups.details.settings.precision">
              {settings.precision.toUpperCase()}
            </SettingRow>

            <SettingRow labelId="deployments.groups.details.settings.interval">
              {settings.interval === 0 ? (
                <FormattedMessage id="deployments.plan.group.everyFrame" />
              ) : (
                <FormattedMessage
                  id="deployments.plan.group.everyNthFrame"
                  values={{ n: settings.interval + 1 }}
                />
              )}
            </SettingRow>

            <SettingRow labelId="deployments.groups.details.settings.tracking">
              <FormattedMessage
                id={`deployments.groups.details.settings.tracking.${trackingState}`}
              />
            </SettingRow>

            <SettingRow labelId="deployments.groups.details.settings.frameSize">
              {settings.frame_width !== null &&
              settings.frame_height !== null ? (
                <FormattedMessage
                  id="deployments.plan.group.frameSize"
                  values={{
                    width: settings.frame_width,
                    height: settings.frame_height
                  }}
                />
              ) : (
                <FormattedMessage id="deployments.groups.details.allowedGpus.none" />
              )}
            </SettingRow>

            <SettingRow labelId="deployments.groups.details.settings.recording">
              <Group gap="xs">
                <Badge variant="light" size="sm">
                  {settings.recording_mode}
                </Badge>
                <FormattedMessage
                  id="deployments.plan.group.clip"
                  values={{
                    pre: settings.pre_event_s,
                    post: settings.post_event_s
                  }}
                />
                {settings.cache_s != null && (
                  <FormattedMessage
                    id="deployments.groups.details.settings.cache"
                    values={{ seconds: settings.cache_s }}
                  />
                )}
              </Group>
            </SettingRow>

            <SettingRow labelId="deployments.groups.details.settings.capacity">
              <FormattedMessage
                id="deployments.plan.group.capacity"
                values={{
                  members: group.members.length,
                  capacity: group.capacity
                }}
              />
            </SettingRow>

            <SettingRow labelId="deployments.groups.details.settings.revision">
              {group.applied_revision ?? group.revision}
            </SettingRow>
          </Table.Tbody>
        </Table>
      </Section>

      <DeploymentGroupLoadCard group={group} device={device} />

      <Text size="xs" c="dimmed">
        <FormattedMessage id="deployments.groups.details.why" />
      </Text>

      <Group gap="xs">
        <Text size="xs" c="dimmed">
          <FormattedMessage id="deployments.groups.details.groupId" />
        </Text>
        <CopyableText text={group.id} iconSize="xs">
          <Text size="xs" ff="monospace">
            {group.id}
          </Text>
        </CopyableText>
      </Group>
    </Stack>
  )
}
